/**
 * useWebauthn — Surface Layer (/01)
 *
 * Passkey register/login orchestration. The relying party (a separate
 * privileged function) returns ceremony options as base64url JSON; this hook
 * converts them to the ArrayBuffers `navigator.credentials` needs, runs the
 * FaceID/TouchID ceremony, converts the result back to JSON, and (for login)
 * redeems the one-time token for a real session. No external WebAuthn library —
 * the conversion is done inline so there is no added dependency.
 */

/* global PublicKeyCredential, AuthenticatorAttestationResponse, AuthenticatorAssertionResponse, AuthenticatorTransport, PublicKeyCredentialDescriptor, PublicKeyCredentialCreationOptions, PublicKeyCredentialRequestOptions, navigator, atob, btoa */

import { useCallback, useState } from 'react';
import {
  webauthnChallenge,
  webauthnRegister,
  webauthnLogin,
  verifyEmailOtp,
} from '@auth-provider';
import { getAccessToken } from '@auth/session-token';

function b64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '==='.slice((padded.length + 3) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface CredDescriptorJSON {
  id: string;
  type?: string;
  transports?: string[];
}

function mapDescriptors(list: CredDescriptorJSON[] | undefined): PublicKeyCredentialDescriptor[] {
  return (list ?? []).map((c) => ({
    id: b64urlToBuffer(c.id),
    type: 'public-key' as const,
    transports: c.transports as AuthenticatorTransport[] | undefined,
  }));
}

function toCreationOptions(opts: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = opts.user as { id: string; name: string; displayName: string };
  return {
    ...(opts as unknown as PublicKeyCredentialCreationOptions),
    challenge: b64urlToBuffer(opts.challenge as string),
    user: { ...user, id: b64urlToBuffer(user.id) },
    excludeCredentials: mapDescriptors(opts.excludeCredentials as CredDescriptorJSON[] | undefined),
  };
}

function toRequestOptions(opts: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  return {
    ...(opts as unknown as PublicKeyCredentialRequestOptions),
    challenge: b64urlToBuffer(opts.challenge as string),
    allowCredentials: mapDescriptors(opts.allowCredentials as CredDescriptorJSON[] | undefined),
  };
}

function shapeAttestation(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      attestationObject: bufferToB64url(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
  };
}

function shapeAssertion(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON),
      authenticatorData: bufferToB64url(r.authenticatorData),
      signature: bufferToB64url(r.signature),
      userHandle: r.userHandle ? bufferToB64url(r.userHandle) : undefined,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
  };
}

/** True when this browser can run a platform-authenticator passkey ceremony. */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials?.create === 'function'
  );
}

/** Specific, safe passkey failure states (operator REQ-001.77-REVISED Stage 1).
 *  Never carries a secret, token, or raw challenge — only a category + safe message. */
export type PasskeyErrorCode =
  | 'session_missing'
  | 'browser_not_supported'
  | 'origin_or_rp_id_mismatch'
  | 'challenge_failed'
  | 'registration_cancelled'
  | 'registration_failed'
  | 'already_registered'
  | 'network_failed'
  | 'unknown_passkey_error';

export interface PasskeyError {
  code: PasskeyErrorCode;
  /** Human-safe message shown in the UI. */
  message: string;
  /** Safe correlation id (random, non-secret) to tie a UI error to logs without leaking anything. */
  correlationId: string;
}

const PASSKEY_MESSAGES: Record<PasskeyErrorCode, string> = {
  session_missing: 'Your secure session expired. Open your e-mail sign-in link again, then add a passkey.',
  browser_not_supported:
    'This browser can’t create a passkey. Try Safari or Chrome on a device with Face ID / Touch ID, or continue with your password.',
  origin_or_rp_id_mismatch:
    'This address isn’t allowed to create a passkey (domain mismatch). Open the app from its official address and retry.',
  challenge_failed: 'The passkey service didn’t respond correctly. Please try again in a moment.',
  registration_cancelled: 'Passkey setup was cancelled. You can try again.',
  registration_failed: 'The passkey couldn’t be saved. Try again, or continue with your password.',
  already_registered: 'A passkey is already set up for this device.',
  network_failed: 'Couldn’t reach the passkey service. Check your connection and retry.',
  unknown_passkey_error: 'Passkey setup didn’t complete. Try again, or continue with your password.',
};

function newCorrelationId(): string {
  const c = (globalThis as {
    crypto?: { randomUUID?: () => string; getRandomValues?: (a: Uint8Array) => Uint8Array };
  }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().slice(0, 8);
  if (c && typeof c.getRandomValues === 'function') {
    return [...c.getRandomValues(new Uint8Array(4))].map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  return 'pk000000';
}

/** Map a WebAuthn ceremony DOMException to a safe, specific state. */
function mapCeremonyException(e: unknown): PasskeyErrorCode {
  const name = (e as { name?: string } | null)?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'AbortError':
      return 'registration_cancelled';
    case 'InvalidStateError':
      return 'already_registered';
    case 'SecurityError':
      return 'origin_or_rp_id_mismatch';
    case 'NotSupportedError':
      return 'browser_not_supported';
    default:
      return 'registration_failed';
  }
}

export interface UseWebauthnResult {
  busy: boolean;
  error: PasskeyError | null;
  /** Register a new passkey for the e-mail-proven (magic-link) session user. */
  register: (email: string) => Promise<boolean>;
  /** Passkey login (usernameless / discoverable). On success a session is established. */
  login: () => Promise<boolean>;
  /** Clear the current error so the user can retry cleanly. */
  reset: () => void;
}

export function useWebauthn(): UseWebauthnResult {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasskeyError | null>(null);

  const reset = useCallback(() => setError(null), []);

  const register = useCallback(async (_email: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const correlationId = newCorrelationId();
    const fail = (code: PasskeyErrorCode): boolean => {
      setError({ code, message: PASSKEY_MESSAGES[code], correlationId });
      return false;
    };
    try {
      if (!isPasskeySupported()) return fail('browser_not_supported');
      // Session-bound enrollment (Auth Flow v2): the relying party derives the
      // user from the Bearer session — an e-mail alone can no longer enroll.
      const token = getAccessToken();
      if (!token) return fail('session_missing');

      const { options, httpStatus } = await webauthnChallenge('register', token);
      if (!options) {
        // Distinguish WHY the challenge failed instead of one generic message.
        if (httpStatus === 0) return fail('network_failed'); // unreachable / function not deployed
        if (httpStatus === 401 || httpStatus === 403) return fail('session_missing'); // fragile / unauthorized
        return fail('challenge_failed'); // 4xx/5xx from the RP
      }

      let cred: PublicKeyCredential | null;
      try {
        cred = (await navigator.credentials.create({
          publicKey: toCreationOptions(options),
        })) as PublicKeyCredential | null;
      } catch (e) {
        return fail(mapCeremonyException(e));
      }
      if (!cred) return fail('registration_cancelled');

      const ok = await webauthnRegister(token, shapeAttestation(cred));
      return ok ? true : fail('registration_failed');
    } catch {
      return fail('unknown_passkey_error');
    } finally {
      setBusy(false);
    }
  }, []);

  const login = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const correlationId = newCorrelationId();
    const fail = (code: PasskeyErrorCode): boolean => {
      setError({ code, message: PASSKEY_MESSAGES[code], correlationId });
      return false;
    };
    try {
      if (!isPasskeySupported()) return fail('browser_not_supported');
      const { options, httpStatus } = await webauthnChallenge('authenticate');
      if (!options) {
        if (httpStatus === 0) return fail('network_failed');
        return fail('challenge_failed');
      }
      let cred: PublicKeyCredential | null;
      try {
        cred = (await navigator.credentials.get({
          publicKey: toRequestOptions(options),
        })) as PublicKeyCredential | null;
      } catch (e) {
        return fail(mapCeremonyException(e));
      }
      if (!cred) return fail('registration_cancelled');

      const result = await webauthnLogin(shapeAssertion(cred));
      if (!result) return fail('registration_failed');
      const session = await verifyEmailOtp(result.tokenHash);
      if (session.error) return fail('registration_failed');
      return true;
    } catch {
      return fail('unknown_passkey_error');
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, register, login, reset };
}
